/** @jsx jsx */
import { useState } from "react"
import Image from "gatsby-image"
import { useSpring, animated as a } from "react-spring"
import { Text, Button, Flex, Box, jsx } from "theme-ui"
import BlockContent from "@sanity/block-content-to-react"

import { Eye, InfoNext, InfoPrev } from "../svg"

const BlockRenderer = props => {
  if (props.node.style === "normal") {
    return (
      <Text variant="secondary" as="p" color="#333">
        {props.children}
      </Text>
    )
  }
  return BlockContent.defaultSerializers.types.block(props)
}

const serializers = {
  types: { block: BlockRenderer, horizontalRule: () => <hr /> },
}

function FlavorNotes({ flavors, index, isOpen, setIsOpen }) {
  const [slideIndex, setSlideIndex] = useState(0)
  const spring = useSpring({
    opacity: isOpen ? 1 : 0,
  })

  const styles = {
    container: {
      position: "relative",
      maxWidth: "600px",
      maxHeight: "315px",
      "@media screen and (max-height: 600px)": {
        maxHeight: "250px",
      },
      "p, button": {
        fontSize: ["18px", null, "16px", "18px"],
      },
      button: {
        padding: "0px",
        background: "transparent",
      },
    },
    header: {
      paddingRight: "50px",
      justifyContent: "space-between",
    },
    controls: {
      display: isOpen ? "flex" : "none",
      zIndex: 1,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      fontFamily: "secondary",
      color: flavors[index].primaryColor,
      div: {
        marginRight: ["0.75em", null, "0.25em", "0.75em"],
      },
      button: {
        display: "flex",
        color: flavors[index].primaryColor,
        padding: "0.25em 0.50em",
      },
    },
    button: {
      zIndex: 1,
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      height: "38px",
      padding: "0.25em",
      border: "none",
      background: "transparent",
      "span, svg": {
        zIndex: 1,
      },
      span: {
        marginLeft: "1.25em",
        textTransform: "uppercase",
        color: isOpen ? flavors[index].primaryColor : "#FFF",
      },
    },
    pane: {
      transform: "translate(-30px,-60px)",
      pointerEvents: isOpen ? "all" : "none",
      background: "#fff",
      padding: "80px 20px 30px",
      boxShadow: "0 0 10px 0 rgba(0,0,0,0.50)",
      borderRadius: "16px",
      height: "auto",
      overflow: "hidden",
    },
    inner: {
      hr: {
        color: "#333",
        opacity: "0.5",
        margin: "1rem 0",
      },
      p: {
        marginTop: "1em",
      },
    },
  }

  return (
    <Box sx={styles.container}>
      <Flex sx={styles.header}>
        <Button
          onClick={() => {
            setIsOpen(!isOpen)
            const indexTimeout = setTimeout(() => {
              setSlideIndex(0)
              clearTimeout(indexTimeout)
            }, 500)
          }}
          sx={styles.button}
        >
          <Eye
            primary={
              isOpen
                ? flavors[index].secondaryColor
                : flavors[index].primaryColor
            }
            secondary={
              isOpen
                ? flavors[index].primaryColor
                : flavors[index].secondaryColor
            }
          />
          <Text as="span" variant="secondary">
            {isOpen ? "CLOSE" : "MORE INFO"}
          </Text>
        </Button>
        <Box sx={styles.controls}>
          <Flex>
            <Button
              onClick={() =>
                setSlideIndex(slideIndex - 1 < 0 ? 0 : slideIndex - 1)
              }
            >
              <InfoPrev color={flavors[index].primaryColor} />
            </Button>
            <Button
              onClick={() =>
                setSlideIndex(slideIndex + 1 > 2 ? 2 : slideIndex + 1)
              }
            >
              <InfoNext color={flavors[index].primaryColor} />
            </Button>
          </Flex>
          <Text as="p">{`[ ${slideIndex + 1} / 3 ]`}</Text>
        </Box>
      </Flex>
      <a.div
        style={{
          ...styles.pane,
          ...spring,
        }}
      >
        <Box sx={styles.inner}>
          <Text
            variant="secondary"
            as="p"
            sx={{ color: flavors[index].primaryColor }}
          >
            {slideIndex === 0 && flavors[index].title.toUpperCase()}
            {slideIndex === 1 && "INGREDIENTS"}
            {slideIndex === 2 && "NUTRITION FACTS"}
          </Text>
          {slideIndex === 0 && (
            <BlockContent
              blocks={flavors[index].description}
              serializers={serializers}
            />
          )}
          {slideIndex === 1 && (
            <Text variant="secondary" as="p" color="#333">
              {flavors[index]?.ingredients?.toUpperCase() || ""}
            </Text>
          )}
          {slideIndex === 2 && (
            <Image
              fluid={
                flavors[index].nutritionInfoImage.localFile.childImageSharp
                  .fluid
              }
              style={{ width: "100%" }}
              alt="Nutrition Facts"
            />
          )}
        </Box>
      </a.div>
    </Box>
  )
}

export default FlavorNotes
